// [BUILDUP] / [WASHOFF] are keyed by a (LandUse, Pollutant) PAIR, not a
// single name — one row per pair that actually has buildup/washoff
// configured.

import { parseNum } from "./parseNum.ts";

export interface BuildupRow {
  landUse: string;
  pollutant: string;
  function: string;
  coeff1: number;
  coeff2: number;
  coeff3: number;
  normalizer: string;
}

export interface WashoffRow {
  landUse: string;
  pollutant: string;
  function: string;
  coeff1: number;
  coeff2: number;
  cleaningEfficiency: number;
  bmpEfficiency: number;
}

export const BUILDUP_FUNCTIONS = ["NONE", "POW", "EXP", "SAT", "EXT"] as const;
export const WASHOFF_FUNCTIONS = ["NONE", "EXP", "RATING", "EMC"] as const;
/** What a buildup amount is normalized against — per unit area, or per unit curb length. */
export const NORMALIZER_TYPES = ["AREA", "CURB"] as const;

export function readBuildup(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): BuildupRow[] {
  return (raw.get("BUILDUP") ?? [])
    .filter((row) => row[0] && row[1])
    .map((row) => {
      const ctx = (field: string) => `[BUILDUP] "${row[0]}" "${row[1]}" ${field}`;
      return {
        landUse: row[0],
        pollutant: row[1],
        function: row[2] ?? "NONE",
        coeff1: parseNum(row[3], 0, ctx("Coeff1"), warnings),
        coeff2: parseNum(row[4], 0, ctx("Coeff2"), warnings),
        coeff3: parseNum(row[5], 0, ctx("Coeff3"), warnings),
        normalizer: row[6] ?? "AREA",
      };
    });
}

export function writeBuildup(
  raw: ReadonlyMap<string, string[][]>,
  rows: BuildupRow[]
): Map<string, string[][]> {
  const out = rows.map((r) => [
    r.landUse,
    r.pollutant,
    r.function,
    String(r.coeff1),
    String(r.coeff2),
    String(r.coeff3),
    r.normalizer,
  ]);
  const next = new Map(raw);
  next.set("BUILDUP", out);
  return next;
}

export function readWashoff(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): WashoffRow[] {
  return (raw.get("WASHOFF") ?? [])
    .filter((row) => row[0] && row[1])
    .map((row) => {
      const ctx = (field: string) => `[WASHOFF] "${row[0]}" "${row[1]}" ${field}`;
      return {
        landUse: row[0],
        pollutant: row[1],
        function: row[2] ?? "NONE",
        coeff1: parseNum(row[3], 0, ctx("Coeff1"), warnings),
        coeff2: parseNum(row[4], 0, ctx("Coeff2"), warnings),
        cleaningEfficiency: parseNum(row[5], 0, ctx("Cleaning Efficiency"), warnings),
        bmpEfficiency: parseNum(row[6], 0, ctx("BMP Efficiency"), warnings),
      };
    });
}

export function writeWashoff(
  raw: ReadonlyMap<string, string[][]>,
  rows: WashoffRow[]
): Map<string, string[][]> {
  const out = rows.map((r) => [
    r.landUse,
    r.pollutant,
    r.function,
    String(r.coeff1),
    String(r.coeff2),
    String(r.cleaningEfficiency),
    String(r.bmpEfficiency),
  ]);
  const next = new Map(raw);
  next.set("WASHOFF", out);
  return next;
}

/** Updates the (landUse, pollutant) row if one already exists, otherwise appends a new one seeded with `defaults`. */
export function upsertRow<T extends { landUse: string; pollutant: string }>(
  rows: T[],
  landUse: string,
  pollutant: string,
  defaults: T,
  patch: Partial<T>
): T[] {
  const index = rows.findIndex((r) => r.landUse === landUse && r.pollutant === pollutant);
  if (index === -1) return [...rows, { ...defaults, landUse, pollutant, ...patch }];
  return rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
}
